/**
 * Fait : l'écran de L'ARGENT — `#/argent`. Tout le monde y lit ce qu'il peut
 *   dépenser et ce qu'il a déjà dépensé ; le parent administrateur y pose en
 *   plus les plafonds de la famille et tient le catalogue des modèles. C'est
 *   le lot 7 du chantier `40.o`, le dernier : les cinq routes de l'argent
 *   n'avaient jusqu'ici aucune porte.
 *
 *   IL EST OUVERT À TOUS, et ce n'est pas une négligence. Un plafond invisible
 *   est la pire des explications quand une demande ne part pas : la personne
 *   croit à une panne. Le serveur, lui, ne rend à un membre ordinaire que le
 *   plafond global et le sien — le tableau familial, les réglages et le
 *   catalogue ne lui arrivent tout simplement pas, et les routes le refusent.
 *
 *   IL RANGE SES PARTIES DERRIÈRE DES ONGLETS pour qui administre — mes
 *   dépenses, la famille, les modèles. Dans les mots de Charles le 2026-08-27 :
 *   « quand tu cliques sur argent, on a des boîtes qui n'en finissent plus dans
 *   le bas de la page ». Quatre blocs empilés, à son fort grossissement, font
 *   une page qu'on parcourt en aveugle. Un membre ordinaire, lui, n'a qu'un
 *   seul bloc : lui poser une barre d'un onglet serait du bruit, et l'écran ne
 *   la montre donc pas.
 *
 *   IL RECHARGE APRÈS CHAQUE ÉCRITURE, et au retour sur l'onglet du navigateur. Ni un budget
 *   ni une entrée de catalogue n'a de canal de diffusion — le direct ne parle
 *   que par projet. Deux parents qui règlent en même temps ne s'avertissent
 *   donc pas ; c'est la VERSION qui rend le conflit lisible, et le rechargement
 *   qui remet l'écran d'aplomb.
 *
 * Ne fait pas : il ne réserve ni ne régularise aucune dépense — cela appartient
 *   au code de génération — et ne décide d'aucun droit : ce qu'il montre est ce
 *   que l'API a bien voulu rendre.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { Annonce } from '../gabarits/Annonce.tsx';
import { Onglets } from '../gabarits/Onglets.tsx';
import { listeLaFamille, type ProfilVu, type CompteDeLaFamille } from './api.ts';
import type { Issue } from './appel.ts';
import {
  inscritUnModele,
  litLeCatalogue,
  litLesBudgets,
  poseLePlafondDunMembre,
  poseLePlafondGlobal,
  retoucheUnModele,
  type BudgetsVus,
  type DemandeDeModele,
  type EntreeDeCatalogueVue,
  type RetoucheDeModele,
} from './api-argent.ts';
import { PartieDeMonBudget } from './PartieDeMonBudget.tsx';
import { enEuros } from './montants.ts';
import { SectionDesPlafonds } from './SectionDesPlafonds.tsx';
import { SectionDuCatalogue } from './SectionDuCatalogue.tsx';

/** Ce que l'écran est en train de faire. */
type Chargement = 'en_cours' | 'pret' | 'injoignable';

/** Les onglets, dans l'ordre où on les rencontre. Le premier est celui d'entrée. */
const ONGLETS_CONNUS = ['mon-budget', 'la-famille', 'les-modeles'] as const;

export function PageArgent({
  profil,
  onglet,
}: {
  readonly profil: ProfilVu;

  /** L'onglet demandé par l'adresse, ou `null` pour celui d'entrée. */
  readonly onglet: string | null;
}) {
  const administre = profil.role === 'family_admin';

  /*
   * L'ONGLET SE LIT DANS L'ADRESSE, ET S'Y ÉCRIT — il ne se garde pas ici.
   *
   * Rangé en mémoire, il disparaissait au moindre rechargement de page : on
   * revenait sur « Mes dépenses » sans l'avoir demandé. Dans l'adresse, il
   * survit au rechargement, se met en favori, et le bouton « précédent » du
   * navigateur repasse d'un onglet à l'autre.
   *
   * UN ONGLET INCONNU RETOMBE SUR LE PREMIER plutôt que de rendre un écran
   * vide : une adresse recopiée de travers ne doit pas faire croire à une
   * panne.
   */
  const ouvert = ONGLETS_CONNUS.find((connu) => connu === onglet) ?? ONGLETS_CONNUS[0];

  const changeDOnglet = (vise: string) => {
    location.hash = vise === ONGLETS_CONNUS[0] ? '#/argent' : `#/argent/${vise}`;
  };

  const [budgets, setBudgets] = useState<BudgetsVus | null>(null);
  const [famille, setFamille] = useState<CompteDeLaFamille[]>([]);
  const [catalogue, setCatalogue] = useState<EntreeDeCatalogueVue[]>([]);

  const [chargement, setChargement] = useState<Chargement>('en_cours');
  const [message, setMessage] = useState<string | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  /** La cible dont l'écriture est en vol — pour n'éteindre que son bouton. */
  const [enVol, setEnVol] = useState<string | null>(null);

  /**
   * LE MESSAGE DOIT VENIR À LA PERSONNE, pas l'inverse.
   *
   * Il naît en haut de l'écran ; les formulaires qui le déclenchent sont tout
   * en bas. Charles a inscrit un modèle le 2026-08-27 et n'a rien vu : « j'ai
   * eu aucune indication comme quoi il était ajouté ». Le message était bien
   * là — hors de vue, ce qui revient au même, et d'autant plus vite qu'on
   * grossit beaucoup.
   *
   * On lui donne donc le FOCUS : la vue s'y porte, et un lecteur d'écran
   * l'annonce à coup sûr plutôt que de compter sur la seule zone vive.
   */
  const surLeMessage = useRef<HTMLParagraphElement>(null);

  const charge = useCallback(async () => {
    setChargement('en_cours');
    const lus = await litLesBudgets();

    if (!lus.bon) {
      setChargement(lus.injoignable === true ? 'injoignable' : 'pret');
      setRefus(lus.erreur.message);
      return;
    }

    setBudgets(lus.valeur);
    setChargement('pret');

    if (!administre) return;

    // Les deux listes du parent. Elles peuvent manquer sans que l'écran
    // devienne inutile : ses propres chiffres, eux, sont déjà affichés.
    const [gens, modeles] = await Promise.all([listeLaFamille(), litLeCatalogue()]);

    if (gens.bon) setFamille(gens.valeur.comptes);
    else setRefus(gens.erreur.message);

    if (modeles.bon) setCatalogue(modeles.valeur.modeles);
    else setRefus(modeles.erreur.message);
  }, [administre]);

  useEffect(() => {
    void charge();
  }, [charge]);

  useEffect(() => {
    const auRetour = () => {
      if (document.visibilityState === 'visible') void charge();
    };

    document.addEventListener('visibilitychange', auRetour);

    return () => document.removeEventListener('visibilitychange', auRetour);
  }, [charge]);

  /**
   * Rend compte d'une écriture, puis recharge — toujours, refus compris. Elle
   * dit si le serveur a accepté : c'est à cette réponse, et non au clic, que
   * les formulaires se referment. Refermer au clic faisait perdre la saisie
   * chaque fois que le serveur refusait — on se retrouvait devant un refus
   * sans plus rien à corriger.
   */
  async function rendCompte(
    cible: string,
    geste: Promise<Issue<unknown>>,
    reussite: string,
  ): Promise<boolean> {
    setEnVol(cible);
    setRefus(null);
    setMessage(null);

    const issue = await geste;
    setEnVol(null);

    if (issue.bon) setMessage(reussite);
    else setRefus(issue.erreur.message);

    await charge();

    /* Après le rechargement, sinon le nœud visé vient d'être remplacé et le
       focus retombe sur le corps du document sans rien annoncer. */
    surLeMessage.current?.focus();

    return issue.bon;
  }

  return (
    /*
     * DES PARTIES SŒURS, JAMAIS IMBRIQUÉES — la règle vaut toujours, y compris
     * dans un panneau d'onglet. Une première version enveloppait tout l'écran
     * dans une partie de plus : à 320 points de large, les rembourrages
     * s'additionnaient et la page débordait de 49 points sur le côté, là où
     * l'écran des consignes, plat, débordait de zéro. Défaut trouvé par une
     * épreuve indépendante le 2026-08-25.
     */
    <div className="empilement" data-ecran="argent">
      {message !== null && (
        <Annonce ref={surLeMessage} tabIndex={-1} data-role="message-de-l-argent">
          {message}
        </Annonce>
      )}

      {refus !== null && (
        <Annonce urgence="urgent" nature="refus" data-role="refus-de-l-argent">
          {refus}
        </Annonce>
      )}

      {chargement === 'en_cours' && (
        <p aria-busy="true" data-etat="chargement">
          Lecture des budgets…
        </p>
      )}

      {chargement === 'injoignable' && (
        <p data-etat="indisponible">
          L’atelier n’a pas répondu. Rien n’a été perdu — rechargez la page.
        </p>
      )}

      {chargement === 'pret' && budgets !== null && !administre && (
        <PartieDeMonBudget budgets={budgets} />
      )}

      {chargement === 'pret' && budgets !== null && administre && (
        <Onglets
          intitule="Les parties de l’argent"
          choisi={ouvert}
          surChoix={changeDOnglet}
          onglets={[
            {
              id: 'mon-budget',
              etiquette: 'Mes dépenses',
              nature: 'projet',
              rendu: () => <PartieDeMonBudget budgets={budgets} />,
            },
            {
              id: 'la-famille',
              etiquette: 'La famille',
              nature: 'partage',
              rendu: () => (
                <SectionDesPlafonds
                  budgets={budgets}
                  famille={famille}
                  enCours={enVol}
                  surPlafondGlobal={(plafondCentimes, version) => {
                    void rendCompte(
                      'global',
                      poseLePlafondGlobal(plafondCentimes, version),
                      `Le plafond de la famille est à ${enEuros(plafondCentimes)}.`,
                    );
                  }}
                  surPlafondDeMembre={(compteId, plafondCentimes, version) => {
                    void rendCompte(
                      compteId,
                      poseLePlafondDunMembre(compteId, plafondCentimes, version),
                      `Le plafond est à ${enEuros(plafondCentimes)}.`,
                    );
                  }}
                />
              ),
            },
            {
              id: 'les-modeles',
              etiquette: 'Les modèles',
              nature: 'travail',
              rendu: () => (
                <SectionDuCatalogue
                  modeles={catalogue}
                  enCours={enVol === 'catalogue'}
                  surInscription={(demande: DemandeDeModele) =>
                    rendCompte(
                      'catalogue',
                      inscritUnModele(demande),
                      `« ${demande.libelle} » est inscrit au catalogue.`,
                    )
                  }
                  surRetouche={(entreeId: string, demande: RetoucheDeModele) =>
                    rendCompte(
                      'catalogue',
                      retoucheUnModele(entreeId, demande),
                      demande.actif === false
                        ? 'Le modèle est éteint : il disparaît du choix de tout le monde.'
                        : 'Le catalogue est à jour.',
                    )
                  }
                />
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
